import { useEffect, useId, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import clsx from 'clsx';

/**
 * Inline help tooltip. Renders a small `i` icon — hover (desktop) or tap
 * (mobile) reveals a short explainer. Keep `body` to 1–3 sentences; for
 * anything longer, link out to docs from the body instead.
 */
export default function InfoTip({ body, title, className = '', side = 'top', size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const id = useId();

  // Close on outside click (so mobile tap-to-open dismisses cleanly).
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const positions = {
    top: 'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 -translate-y-1/2',
  };

  const iconSize = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  return (
    <span
      ref={wrapRef}
      className={clsx('relative inline-flex items-center align-middle', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-describedby={id}
        aria-label={title || 'More info'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex items-center justify-center rounded-full text-ink-400 hover:text-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      >
        <Info className={iconSize} strokeWidth={2} />
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          className={clsx(
            'absolute z-50 w-64 rounded-xl bg-ink-800 text-white shadow-xl text-xs leading-relaxed p-3 normal-case font-normal tracking-normal',
            positions[side] || positions.top,
          )}
        >
          {title && <span className="block font-semibold mb-1 text-white">{title}</span>}
          <span className="block text-white/85">{body}</span>
        </span>
      )}
    </span>
  );
}
